import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { setStrategy } from '../../../redux/actions'
import { useAuth0 } from '../../../contexts/auth0-context'
import callAPI from '../../../API/callSecureAPI'
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    CircularProgress,
    TextField,
    Button,
} from '@mui/material'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import {
    useAdminActivityFeedQuery,
    useAdminOrganizationsQuery,
    useAdminRoadmapsByOrgQuery,
    ActivityFeedItem,
} from '../../../shared/queries/admin'

const ActivityFeedTab: React.FC = () => {
    const [page, setPage] = useState(1)
    const [actionFilter, setActionFilter] = useState<string>('')
    const [orgFilter, setOrgFilter] = useState<string>('')
    const [roadmapFilter, setRoadmapFilter] = useState<string>('')
    const [startDate, setStartDate] = useState<string>('')
    const [endDate, setEndDate] = useState<string>('')
    const [selectedItem, setSelectedItem] = useState<ActivityFeedItem | null>(null)
    const [isNavigating, setIsNavigating] = useState(false)
    const [navError, setNavError] = useState<string | null>(null)
    const [isExportingHtml, setIsExportingHtml] = useState(false)
    const { data, isLoading } = useAdminActivityFeedQuery(
        page,
        100,
        actionFilter || undefined,
        orgFilter || undefined,
        startDate || undefined,
        endDate || undefined,
        roadmapFilter || undefined
    )
    const { data: orgsData } = useAdminOrganizationsQuery(1, 200)
    const { data: roadmapsData } = useAdminRoadmapsByOrgQuery(orgFilter, 1, 200)
    const { getTokenSilently } = useAuth0()
    const history = useHistory()
    const dispatch = useDispatch()

    const feed = data?.data?.feed || []
    const actionTypes = data?.data?.actionTypes || []
    const pagination = data?.data?.pagination

    // Normalize item type to standard format
    const normalizeItemType = (type: string | null): string | null => {
        if (!type) return null
        const normalized = type.toLowerCase().replace(/\s+/g, '')
        if (normalized === 'valuemessage' || normalized === 'vm') return 'ValueMessage'
        if (normalized === 'evidence' || normalized === 'ev') return 'Evidence'
        if (normalized === 'activity' || normalized === 'act') return 'Activity'
        return type
    }

    // Get item type from metadata
    const getItemType = (item: ActivityFeedItem): string | null => {
        const metadata = item.metadata as Record<string, unknown> | null
        const rawType = item.itemType || (metadata?.type as string) || (metadata?.itemType as string) || null
        return normalizeItemType(rawType)
    }

    // Get content ID from metadata
    const getContentId = (item: ActivityFeedItem): string | null => {
        const metadata = item.metadata as Record<string, unknown> | null
        if (!metadata) return null
        // Try various places the ID might be stored
        const data = metadata.data as Record<string, unknown> | null
        const to = metadata.to as Record<string, unknown> | null
        const from = metadata.from as Record<string, unknown> | null
        return (
            (metadata._id as string) || // VIEW events store _id directly
            (metadata.contentId as string) ||
            (metadata.itemId as string) ||
            (to?._id as string) || // CREATE/UPDATE events
            (data?._id as string) ||
            (from?._id as string) ||
            null
        )
    }

    // Get item name from metadata for fallback search
    const getItemName = (item: ActivityFeedItem): string | null => {
        if (item.itemName) return item.itemName
        const metadata = item.metadata as Record<string, unknown> | null
        if (!metadata) return null
        const data = metadata.data as Record<string, unknown> | null
        const to = metadata.to as Record<string, unknown> | null
        const from = metadata.from as Record<string, unknown> | null
        return (
            (data?.name as string) ||
            (data?.title as string) ||
            (to?.name as string) ||
            (to?.title as string) ||
            (from?.name as string) ||
            (from?.title as string) ||
            null
        )
    }

    // Check if item is navigable (not DELETE events - item won't exist)
    const isNavigable = (item: ActivityFeedItem): boolean => {
        // Don't show nav for DELETE - item no longer exists
        if (item.action.includes('DELETE')) return false
        if (!item.roadmap?._id) return false
        const itemType = getItemType(item)
        if (!itemType) return false
        const navigableTypes = ['ValueMessage', 'Evidence', 'Activity']
        if (!navigableTypes.includes(itemType)) return false
        // Must have either content ID or a name to search by
        const contentId = getContentId(item)
        const itemName = getItemName(item)
        return !!(contentId || itemName)
    }

    // Navigate to item
    const handleNavigateToItem = async (item: ActivityFeedItem) => {
        if (!item.roadmap?._id) return

        const itemType = getItemType(item)

        if (!itemType) return

        setIsNavigating(true)

        try {
            const token = await getTokenSilently?.({ audience: 'https://atlas.aesara.com' })

            // Fetch full strategy
            const strategyResponse = await callAPI(`/api/strategy/${item.roadmap._id}`, 'get', token)

            if (!strategyResponse?.data) return

            dispatch(setStrategy(strategyResponse.data))

            let itemData: Record<string, unknown> | null = null
            const contentId = getContentId(item)

            // Fetch the full item
            if (contentId) {
                if (itemType === 'ValueMessage') {
                    const vmResponse = await callAPI(`/api/value-message/${contentId}`, 'get', token)
                    if (vmResponse?.data) itemData = vmResponse.data
                } else if (itemType === 'Evidence') {
                    const evResponse = await callAPI(`/api/evidence/${contentId}`, 'get', token)
                    if (evResponse?.data) itemData = evResponse.data
                } else if (itemType === 'Activity') {
                    const actResponse = await callAPI(`/api/activity/${contentId}`, 'get', token)
                    if (actResponse?.data) itemData = actResponse.data
                }
            }

            // Fallback: search by name
            const searchName = getItemName(item)
            if (!itemData && searchName) {
                if (itemType === 'ValueMessage') {
                    const vmsResponse = await callAPI(`/api/value-messages/${item.roadmap._id}`, 'get', token)
                    if (vmsResponse?.data) {
                        const matches = vmsResponse.data.filter((vm: { name?: string }) => vm.name === searchName)
                        if (matches.length === 1) itemData = matches[0]
                    }
                } else if (itemType === 'Evidence') {
                    const evResponse = await callAPI(`/api/evidences/${item.roadmap._id}`, 'get', token)
                    if (evResponse?.data) {
                        const matches = evResponse.data.filter((ev: { name?: string }) => ev.name === searchName)
                        if (matches.length === 1) itemData = matches[0]
                    }
                } else if (itemType === 'Activity') {
                    const actResponse = await callAPI(`/api/activities/${item.roadmap._id}`, 'get', token)
                    if (actResponse?.data) {
                        const matches = actResponse.data.filter((act: { title?: string }) => act.title === searchName)
                        if (matches.length === 1) itemData = matches[0]
                    }
                }
            }

            if (!itemData) {
                setNavError('Item not found - it may have been deleted')
                setIsNavigating(false)
                return
            }
            setNavError(null)

            setSelectedItem(null)

            const routes: Record<string, string> = {
                ValueMessage: '/narratives',
                Evidence: '/evidence',
                Activity: '/activities',
            }
            const pathname = routes[itemType]

            if (pathname) {
                history.push({
                    pathname,
                    // @ts-ignore - custom location state property
                    autoOpenData: {
                        isAutoOpenPanel: true,
                        autoOpenData: itemData,
                        previousDataStack: [
                            {
                                path: '/admin',
                                isAutoOpenPanel: false,
                                data: { tabIndex: 2 }, // Activity Feed tab
                            },
                        ],
                    },
                })
            }
        } catch (err) {
            setNavError('An error occurred while navigating')
        } finally {
            setIsNavigating(false)
        }
    }

    const formatTimestamp = (dateString: string) => {
        if (!dateString) return ''
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return ''
        const now = new Date()
        const diffMs = now.getTime() - date.getTime()
        const diffMins = Math.floor(diffMs / (1000 * 60))
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

        if (diffMins < 1) return 'Just now'
        if (diffMins < 60) return `${diffMins}m ago`
        if (diffHours < 24) return `${diffHours}h ago`
        if (diffDays < 7) return `${diffDays}d ago`
        return date.toLocaleDateString()
    }

    const getActionColor = (action: string): 'success' | 'info' | 'warning' | 'error' | 'default' => {
        if (action.includes('CREATE')) return 'success'
        if (action.includes('UPDATE')) return 'info'
        if (action.includes('DELETE')) return 'error'
        if (action.includes('VIEW')) return 'default'
        return 'default'
    }

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box>
            {/* Filters — row 1: dropdowns */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Filter by Action</InputLabel>
                    <Select
                        value={actionFilter}
                        label="Filter by Action"
                        onChange={(e) => {
                            setActionFilter(e.target.value)
                            setPage(1)
                        }}
                    >
                        <MenuItem value="">All Actions</MenuItem>
                        {actionTypes.map((action) => (
                            <MenuItem key={action} value={action}>
                                {action}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Organization</InputLabel>
                    <Select
                        value={orgFilter}
                        label="Organization"
                        onChange={(e) => {
                            setOrgFilter(e.target.value)
                            setRoadmapFilter('')
                            setPage(1)
                        }}
                    >
                        <MenuItem value="">All Organizations</MenuItem>
                        {(orgsData?.data?.organizations || []).map((org) => (
                            <MenuItem key={org._id} value={org._id}>
                                {org.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 200 }} disabled={!orgFilter}>
                    <InputLabel>Roadmap</InputLabel>
                    <Select
                        value={roadmapFilter}
                        label="Roadmap"
                        onChange={(e) => {
                            setRoadmapFilter(e.target.value)
                            setPage(1)
                        }}
                    >
                        <MenuItem value="">All Roadmaps</MenuItem>
                        {(roadmapsData?.data?.roadmaps || []).map((r) => (
                            <MenuItem key={r._id} value={r._id}>
                                {r.product} — {r.indication}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography variant="body2" color="figma.grayscale.textHier2" sx={{ ml: 'auto' }}>
                    {pagination?.total.toLocaleString()} events
                </Typography>
            </Box>

            {/* Filters — row 2: date range + exports */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    label="From"
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                        setStartDate(e.target.value)
                        setPage(1)
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 160 }}
                />
                <TextField
                    size="small"
                    label="To"
                    type="date"
                    value={endDate}
                    onChange={(e) => {
                        setEndDate(e.target.value)
                        setPage(1)
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 160 }}
                />
                {[
                    { label: 'Last 7d', days: 7 },
                    { label: 'Last 30d', days: 30 },
                    { label: 'Last 90d', days: 90 },
                    { label: 'Last Year', days: 365 },
                ].map(({ label, days }) => (
                    <Chip
                        key={days}
                        label={label}
                        size="small"
                        variant={
                            startDate === new Date(Date.now() - days * 86400000).toISOString().slice(0, 10) &&
                            endDate === new Date().toISOString().slice(0, 10)
                                ? 'filled'
                                : 'outlined'
                        }
                        onClick={() => {
                            setStartDate(new Date(Date.now() - days * 86400000).toISOString().slice(0, 10))
                            setEndDate(new Date().toISOString().slice(0, 10))
                            setPage(1)
                        }}
                        sx={{ cursor: 'pointer' }}
                    />
                ))}
                <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={feed.length === 0}
                        onClick={() => {
                            const rows = feed.map((item) => ({
                                Timestamp: item.timestamp,
                                Action: item.action,
                                'User Name': item.user?.name ?? '',
                                'User Email': item.user?.email ?? '',
                                Organization: item.organization?.name ?? '',
                                Roadmap: item.roadmap ? `${item.roadmap.product} - ${item.roadmap.indication}` : '',
                                'Item Type': item.itemType ?? '',
                                'Item Name': item.itemName ?? '',
                            }))
                            const headers = Object.keys(rows[0])
                            const csv = [
                                headers.join(','),
                                ...rows.map((r) =>
                                    headers.map((h) => JSON.stringify((r as Record<string, string>)[h] ?? '')).join(',')
                                ),
                            ].join('\n')
                            const blob = new Blob([csv], { type: 'text/csv' })
                            const url = URL.createObjectURL(blob)
                            const a = document.createElement('a')
                            a.href = url
                            a.download = 'activity-feed.csv'
                            a.click()
                            URL.revokeObjectURL(url)
                        }}
                    >
                        Export CSV
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={feed.length === 0 || isExportingHtml}
                        onClick={async () => {
                            setIsExportingHtml(true)
                            try {
                                const token = await getTokenSilently?.({ audience: 'https://atlas.aesara.com' })
                                const params = new URLSearchParams()
                                if (actionFilter) params.set('action', actionFilter)
                                if (orgFilter) params.set('orgId', orgFilter)
                                if (roadmapFilter) params.set('roadmapId', roadmapFilter)
                                if (startDate) params.set('startDate', startDate)
                                if (endDate) params.set('endDate', endDate)
                                const res = await fetch(`/api/admin/activity-feed/export?${params.toString()}`, {
                                    headers: { Authorization: `Bearer ${token}` },
                                })
                                const html = await res.text()
                                const blob = new Blob([html], { type: 'text/html' })
                                const blobUrl = URL.createObjectURL(blob)
                                const a = document.createElement('a')
                                const dateSuffix = startDate && endDate ? `-${startDate}-${endDate}` : ''
                                a.href = blobUrl
                                a.download = `activity-feed${dateSuffix}.html`
                                a.click()
                                URL.revokeObjectURL(blobUrl)
                            } finally {
                                setIsExportingHtml(false)
                            }
                        }}
                    >
                        {isExportingHtml ? 'Exporting…' : 'Export HTML'}
                    </Button>
                </Box>
            </Box>

            {/* Feed */}
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell width={120}>Time</TableCell>
                            <TableCell width={100}>Action</TableCell>
                            <TableCell>User</TableCell>
                            <TableCell>Organization</TableCell>
                            <TableCell>Roadmap</TableCell>
                            <TableCell>Item</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {feed.map((item: ActivityFeedItem) => (
                            <TableRow
                                key={item._id}
                                hover
                                onClick={() => setSelectedItem(item)}
                                sx={{ cursor: 'pointer' }}
                            >
                                <TableCell>
                                    <Tooltip
                                        title={
                                            item.timestamp
                                                ? new Date(item.timestamp).toLocaleString(undefined, {
                                                      timeZoneName: 'short',
                                                  })
                                                : ''
                                        }
                                    >
                                        <Typography variant="caption" color="figma.grayscale.textHier2">
                                            {formatTimestamp(item.timestamp)}
                                        </Typography>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={item.action}
                                        size="small"
                                        color={getActionColor(item.action)}
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem' }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                        {item.user?.name || item.user?.email || '—'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                        {item.organization?.name || '—'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                        {item.roadmap ? `${item.roadmap.product} - ${item.roadmap.indication}` : '—'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    {item.itemType && (
                                        <Box>
                                            <Typography variant="caption" color="figma.grayscale.textHier2">
                                                {item.itemType}
                                            </Typography>
                                            {item.itemName && (
                                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                    {item.itemName}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                    <Chip
                        label="Previous"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        variant="outlined"
                        clickable
                    />
                    <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                        Page {page} of {pagination.totalPages}
                    </Typography>
                    <Chip
                        label="Next"
                        onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                        disabled={page === pagination.totalPages}
                        variant="outlined"
                        clickable
                    />
                </Box>
            )}

            {/* Detail Dialog */}
            <Dialog
                open={!!selectedItem}
                onClose={() => {
                    setSelectedItem(null)
                    setNavError(null)
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Chip
                            label={selectedItem?.action}
                            size="small"
                            color={selectedItem ? getActionColor(selectedItem.action) : 'default'}
                            sx={{ mr: 1 }}
                        />
                        <Typography component="span" variant="body2" color="figma.grayscale.textHier2">
                            {selectedItem?.timestamp
                                ? new Date(selectedItem.timestamp).toLocaleString(undefined, { timeZoneName: 'short' })
                                : ''}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedItem && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Summary Info */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                <Box>
                                    <Typography variant="caption" color="figma.grayscale.textHier2">
                                        User
                                    </Typography>
                                    <Typography variant="body2">
                                        {selectedItem.user?.name || selectedItem.user?.email || '—'}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="figma.grayscale.textHier2">
                                        Organization
                                    </Typography>
                                    <Typography variant="body2">{selectedItem.organization?.name || '—'}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="figma.grayscale.textHier2">
                                        Roadmap
                                    </Typography>
                                    <Typography variant="body2">
                                        {selectedItem.roadmap
                                            ? `${selectedItem.roadmap.product} - ${selectedItem.roadmap.indication}`
                                            : '—'}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="figma.grayscale.textHier2">
                                        Item
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2">
                                            {selectedItem.itemType
                                                ? `${selectedItem.itemType}: ${selectedItem.itemName || '—'}`
                                                : '—'}
                                        </Typography>
                                        {isNavigable(selectedItem) && (
                                            <Chip
                                                label={isNavigating ? 'Opening...' : 'Open'}
                                                size="small"
                                                color={navError ? 'error' : 'primary'}
                                                onClick={() => {
                                                    setNavError(null)
                                                    handleNavigateToItem(selectedItem)
                                                }}
                                                disabled={isNavigating}
                                                icon={
                                                    isNavigating ? (
                                                        <CircularProgress size={12} />
                                                    ) : (
                                                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                                                    )
                                                }
                                                clickable
                                            />
                                        )}
                                        {navError && (
                                            <Typography variant="caption" color="error">
                                                {navError}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>

                            {/* Full Metadata */}
                            {selectedItem.metadata && Object.keys(selectedItem.metadata).length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Event Data
                                    </Typography>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 2,
                                            bgcolor: 'figma.grayscale.table1',
                                            maxHeight: 400,
                                            overflow: 'auto',
                                        }}
                                    >
                                        <pre
                                            style={{
                                                margin: 0,
                                                fontSize: '12px',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            {JSON.stringify(selectedItem.metadata, null, 2)}
                                        </pre>
                                    </Paper>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    )
}

export default ActivityFeedTab
